/* global document */

import smoothscroll from 'smoothscroll-polyfill';
import React from 'react';
import PropTypes from 'prop-types';
import Banner from './components/Banner';
import Breadcrumb from './components/Breadcrumb';
import CategoriesSlider from './components/CategoriesSlider';
import Item from './components/Item';
import Map from './components/Map';

smoothscroll.polyfill();

class MapExplorer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedItemId: null,
      sortingMode: 'distance',
      zoom: 6,
    };
  }

  onZoomChanged(zoom) {
    this.setState({ zoom });
  }

  onMarkerClick(id) {
    const { selectedItemId } = this.state;
    const isCurrentlySelected = selectedItemId ? (selectedItemId === id) : false;

    if (isCurrentlySelected) {
      this.setState({ selectedItemId: null });
    } else {
      const item = this.findItem(id);
      this.setState({ selectedItemId: id });
      document.querySelector(`[id='${item.slug}']`).scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  onItemMapClick(event, id) {
    const itemIsOnMap = this.findItem(id).showOnMap;
    if (itemIsOnMap) {
      this.setState({ selectedItemId: id });
    }
    event.preventDefault();
    document.querySelector('#map').scrollIntoView({
      behavior: 'smooth',
    });
  }

  allItems() {
    const { categories } = this.props;
    let items = [];
    categories.forEach((category) => {
      items = items.concat(category.items);
    });
    return items;
  }

  findItem(id) {
    return this.allItems().find(element => element.id === id);
  }

  itemsForMap() {
    return this.allItems().filter(item => item.showOnMap);
  }

  renderCategories() {
    const { categories } = this.props;
    const shouldDisplayTitle = this.props.displayCategories;

    return categories.map((category, index) =>
      this.renderCategory(shouldDisplayTitle, category, index === 0)
    );
  }

  itemsSortedByDistance(items) {
    return items.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
  }

  itemsSortedByAlpha(items) {
    return items.sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      return 0;
    });
  }

  sortedItems(items) {
    const { sortingMode } = this.state;

    if (sortingMode === 'distance') {
      return this.itemsSortedByDistance(items);
    }

    return this.itemsSortedByAlpha(items);
  }

  sortChanged(event) {
    this.setState({
      sortingMode: event.target.value,
    });
  }

  renderCategory(shouldDisplayTitle, category, withSorter) {
    let renderedTitle = null;
    let renderedSorter = null;
    const { id, title, url, items } = category;
    const itemsAfterSorting = this.sortedItems(items);
    const { selectedItemId } = this.state;
    const key = `category-${id}`;

    if (shouldDisplayTitle) {
      renderedTitle = (
        <h2 className="map-explorer__categories__category__header__title">
          <a href={url}>{title}</a>
        </h2>
      );
    }

    if (withSorter) {
      renderedSorter = (
        <select onChange={event => this.sortChanged(event)} className="map-explorer__categories__category__header__sort">
          <option value="distance">Sort by: Distance</option>
          <option value="alphabetical">Sort by: Alphabetical</option>
        </select>
      )
    }

    return (
      <div key={key}>
        <div className="map-explorer__categories__category__header">
          {renderedTitle}
          {renderedSorter}
        </div>
        <div className="map-explorer__categories__category__items">
          {itemsAfterSorting.map((item) => {
            const itemIsSelected = selectedItemId === item.id;

            return (
              <Item
                selected={itemIsSelected}
                key={item.id}
                {...item}
                onMapClick={event => this.onItemMapClick(event, item.id)}
                category={category}
              />
            );
          })}
        </div>
      </div>
    );
  }

  renderCategoriesSlider() {
    const { displayCategories, categories } = this.props;

    if (!displayCategories) { return null; }

    return (
      <CategoriesSlider categories={categories} />
    );
  }

  renderBreadcrumbTrail() {
    const { header: { rootTitle, rootUrl, title, url } } = this.props;

    if (!rootTitle) { return null; }

    return (
      <p className="map-explorer__header__breadcrumbs">
        <Breadcrumb className="map-explorer__header__breadcrumbs--root" text={rootTitle} url={rootUrl} />
        <Breadcrumb className="map-explorer__header__breadcrumbs--current" text={title} url={url} />
      </p>
    );
  }

  render() {
    const { centerPoint, header: { title, imageUrl: bannerImageUrl } } = this.props;
    const { zoom, selectedItemId } = this.state;

    return (
      <div className="map-explorer">
        <div className="map-explorer__main">
          <div className="map-explorer__header">
            <Banner imageUrl={bannerImageUrl} />
            {this.renderBreadcrumbTrail()}
            <h1 className="map-explorer__header__title">{title}</h1>
            {this.renderCategoriesSlider()}
          </div>

          <div className="map-explorer__categories">
            {this.renderCategories()}
          </div>
        </div>

        <div id="map" className="map-explorer__map">
          <Map
            onZoomChanged={newZoom => this.onZoomChanged(newZoom)}
            onMarkerClick={newSelectedItemId => this.onMarkerClick(newSelectedItemId)}
            zoom={zoom}
            selectedItemId={selectedItemId}
            centerPoint={centerPoint}
            items={this.itemsForMap()}
          />
        </div>
      </div>
    );
  }
}

MapExplorer.defaultProps = {
  header: {
    imageUrl: null,
    rootTitle: null,
    rootUrl: null,
  },
  displayCategories: false,
  categories: [],
};

MapExplorer.propTypes = {
  header: PropTypes.shape({
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    rootTitle: PropTypes.string,
    rootUrl: PropTypes.string,
  }).isRequired,
  centerPoint: PropTypes.shape({
    longitude: PropTypes.number.isRequired,
    latitude: PropTypes.number.isRequired,
    iconUrl: PropTypes.string.isRequired,
  }).isRequired,
  displayCategories: PropTypes.bool,
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    thumbnailUrl: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      description: PropTypes.string.isRequired,
      distance: PropTypes.string,
      showOnMap: PropTypes.bool.isRequired,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      websiteUrl: PropTypes.string,
    })),
  })),
};

export default MapExplorer;
